<script setup lang="ts">
import { useDropZone } from '@vueuse/core'
import { ElMessage } from 'element-plus'
import { defineEmits, defineProps, ref } from 'vue'

const props = defineProps({
  accept: {
    type: String,
    default: '*/*',
  },
  multiple: {
    type: Boolean,
    default: true,
  },
  maxSize: {
    type: Number,
    default: 10 * 1024 * 1024, // 默认10MB
  },
})

const emit = defineEmits(['fileUploaded'])

// 拖放区域的引用
const dropZoneRef = ref<HTMLElement | null>(null)

// 使用 VueUse 的 useDropZone
const { isOverDropZone } = useDropZone(dropZoneRef, onDrop)

// 处理拖放的文件
function onDrop(files: File[] | null) {
  if (!files || files.length === 0)
    return
  handleFiles(Array.from(files))
}

// 处理文件输入框选择的文件
function handleFileInput(e: Event) {
  const target = e.target as HTMLInputElement
  if (!target.files)
    return

  const files = Array.from(target.files)
  handleFiles(files)
}

// 处理文件
function handleFiles(files: File[]) {
  files.forEach((file) => {
    // 检查文件大小
    if (file.size > props.maxSize) {
      ElMessage.error(`文件 ${file.name} 过大，最大允许 ${formatFileSize(props.maxSize)}`)
      return
    }

    const reader = new FileReader()
    reader.onload = (event) => {
      if (!event.target || typeof event.target.result !== 'string')
        return

      // 提取 base64 内容（去除 data:类型;base64, 前缀）
      const base64Content = event.target.result.split(',')[1]

      // 向父组件发送文件数据
      emit('fileUploaded', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type || 'application/octet-stream',
        fileContent: base64Content,
      })
    }

    reader.readAsDataURL(file)
  })
}

// 格式化文件大小
function formatFileSize(size: number): string {
  if (size < 1024)
    return `${size} B`
  else if (size < 1024 * 1024)
    return `${(size / 1024).toFixed(2)} KB`
  else return `${(size / (1024 * 1024)).toFixed(2)} MB`
}
</script>

<template>
  <div class="file-uploader">
    <div
      ref="dropZoneRef"
      class="drag-area"
      :class="{ 'is-over': isOverDropZone }"
    >
      <el-icon><el-icon-upload /></el-icon>
      <div class="drag-text">
        拖拽文件到此处或
      </div>
      <el-button type="primary" size="small">
        点击上传
        <input
          type="file"
          class="file-input"
          :multiple="multiple"
          :accept="accept"
          @change="handleFileInput"
        >
      </el-button>
      <slot name="tip">
        <div class="drag-tip">
          点击或拖拽文件到此区域上传
        </div>
      </slot>
    </div>
  </div>
</template>

<style scoped>
.file-uploader {
  margin: 20px 0;
}

.drag-area {
  border: 2px dashed #dcdfe6;
  border-radius: 6px;
  text-align: center;
  padding: 20px;
  cursor: pointer;
  position: relative;
  transition: all 0.3s;
}

.drag-area.is-over {
  border-color: #409eff;
  background-color: rgba(64, 158, 255, 0.1);
}

.drag-text {
  margin: 10px 0;
  color: #606266;
}

.drag-tip {
  margin-top: 10px;
  font-size: 12px;
  color: #909399;
}

.file-input {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}
</style>
